import React from 'react';
import { cn } from '@/lib/utils';
import { useCustomHorizontalScroll } from '@/hooks/useCustomHorizontalScroll';
import { ChevronsLeftIcon, ChevronsRightIcon } from 'lucide-react';

export interface HorizontalScrollContainerProps {
  children: React.ReactNode;
  className?: string;
  scrollbarClassName?: string;
  thumbClassName?: string;
  trackClassName?: string;
  showScrollbar?: boolean;
}

export function HorizontalScrollContainer({
  children,
  className,
  scrollbarClassName,
  thumbClassName,
  trackClassName,
  showScrollbar = true,
}: HorizontalScrollContainerProps) {
  const {
    containerRef,
    scrollbarRef,
    thumbRef,
    trackRef,
    isDragging,
    isHovered,
    thumbWidth,
    thumbPosition,
    showScrollbar: needsScrollbar,
    handleThumbMouseDown,
    handleTrackClick,
    handleThumbMouseEnter,
    handleThumbMouseLeave,
  } = useCustomHorizontalScroll();

  return (
    <div className="relative w-full">
      <div
        ref={containerRef}
        className={cn(
          'overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          className,
        )}
        style={{
          scrollBehavior: isDragging ? 'auto' : 'smooth',
        }}
      >
        {children}
      </div>

      {showScrollbar && needsScrollbar && (
        <div
          ref={scrollbarRef}
          className={cn('relative mt-2 h-4 w-full', scrollbarClassName)}
        >
          {/* Track */}
          <div
            ref={trackRef}
            className={cn(
              'absolute inset-0 cursor-pointer rounded-full border border-gray-300',
              trackClassName,
            )}
            onClick={handleTrackClick}
            role="scrollbar"
            aria-orientation="horizontal"
            aria-valuenow={thumbPosition}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {/* Thumb */}
            <div
              ref={thumbRef}
              className={cn(
                'absolute top-1/2 flex h-2.5 -translate-y-1/2 cursor-grab items-center justify-between gap-8 rounded-full bg-gray-300 transition-colors',
                {
                  'bg-primary/70 cursor-grab': isHovered,
                  'bg-primary cursor-grabbing': isDragging,
                },
                thumbClassName,
              )}
              style={{
                width: `${thumbWidth}px`,
                left: `${thumbPosition > 0 ? thumbPosition - 2 : 2}px`,
              }}
              onMouseDown={handleThumbMouseDown}
              onMouseEnter={handleThumbMouseEnter}
              onMouseLeave={handleThumbMouseLeave}
              role="button"
              aria-label="Scroll thumb"
              tabIndex={0}
              onKeyDown={event => {
                const container = containerRef.current;

                if (!container) return;

                const scrollAmount = 100;

                switch (event.key) {
                  case 'ArrowLeft':
                    event.preventDefault();
                    container.scrollLeft -= scrollAmount;
                  case 'ArrowRight':
                    event.preventDefault();
                    container.scrollLeft += scrollAmount;
                  case 'Home':
                    event.preventDefault();
                    container.scrollLeft = 0;
                  case 'End':
                    event.preventDefault();
                    container.scrollLeft = container.scrollWidth - container.clientWidth;
                }
              }}
            >
              <ChevronsLeftIcon
                className={cn('h-full', {
                  'text-white': isHovered || isDragging,
                })}
              />
              <ChevronsRightIcon
                className={cn('h-full', {
                  'text-white': isHovered || isDragging,
                })}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
